import React, { useMemo } from 'react';
import { useLocation } from 'react-router';
import { Link } from 'react-router-dom';
import classnames from 'classnames/bind';
import PropTypes from 'prop-types';
import { ACCOUNT_ID } from 'hooks/queryParams';
import { useMetaKeySkipHandler } from 'hooks/useMetaKeySkipHandler';
import { DATE_PRESET, END_DATE, START_DATE } from 'modules/campaigns/constants/queryParams';
import { mergeQueryParams, pickQueryParams } from 'modules/taboola-common-frontend-modules/query-params';
import { DRAWER_STACK, useDrawerStack } from '../../hooks/queryParams/useDrawerStack';
import { hasFormMode } from '../../modules/taboola-common-frontend-modules/forms/utils/hasFormMode';
import {
    getLinkAuxHandler,
    getOnClickHandler,
} from '../../modules/taboola-common-frontend-modules/iframe/utils/linkHandlers';
import { useMultiLayerDrawerEnabled } from '../../modules/taboola-common-frontend-modules/multi-layer-drawer/hooks/useMultiLayerDrawerEnabled';
import { useInternalLinkMenu } from './useInternalLinkMenu';
import styles from './InternalLink.module.scss';

const classNameBuilder = classnames.bind(styles);

export const PARAMS_TO_KEEP = [START_DATE, END_DATE, DATE_PRESET, ACCOUNT_ID];
export const InternalLink = ({
    path,
    params,
    paramsToKeep = PARAMS_TO_KEEP,
    className,
    disabled,
    excludeDrawerStack,
    ...rest
}) => {
    const locationWithRequiredParams = pickQueryParams(paramsToKeep);
    const isMultiLayerDrawerEnabled = useMultiLayerDrawerEnabled();
    const location = useLocation();
    const { stack } = useDrawerStack();

    const pathname = path ?? location.pathname;

    const queryParams = useMemo(() => {
        if (!isMultiLayerDrawerEnabled || !hasFormMode(pathname)) {
            return mergeQueryParams(locationWithRequiredParams, params);
        }

        return mergeQueryParams(locationWithRequiredParams, {
            ...params,
            ...(excludeDrawerStack
                ? {
                      [DRAWER_STACK]: stack,
                  }
                : {
                      [DRAWER_STACK]: [...stack, { path: pathname }],
                  }),
        });
    }, [stack, params, locationWithRequiredParams, isMultiLayerDrawerEnabled, pathname, excludeDrawerStack]);

    const to = `${pathname}${queryParams}`;

    const onClick = useMetaKeySkipHandler(rest?.onClick);

    const openContextMenu = useInternalLinkMenu(to);

    return (
        <Link
            to={to}
            {...rest}
            onClick={getOnClickHandler(onClick)}
            onAuxClick={getLinkAuxHandler(rest?.onAuxClick)}
            className={classNameBuilder(className, { disabled })}
            onContextMenu={openContextMenu}
        />
    );
};

InternalLink.propTypes = {
    children: PropTypes.node,
    path: PropTypes.string,
    params: PropTypes.object,
    paramsToKeep: PropTypes.array,
    disabled: PropTypes.bool,
};
